import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnlineStatus } from '../lib/supabase/hooks/useOnlineStatus';
import { syncStatusManager } from '../hooks/useSyncStatus';
import { syncStatus as pendingSyncStatus } from '../lib/supabase/utils/syncStatus';

const SyncIndicator = ({ onRetry }) => {
  const { isOnline, hasPending, pendingCount } = useOnlineStatus();
  const [syncStatus, setSyncStatus] = useState({
    isSyncing: false,
    pendingOperations: Object.values(pendingSyncStatus.getPending())
  });
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const unsubscribe = syncStatusManager.subscribe((status) => {
      setSyncStatus({
        ...status,
        pendingOperations: Object.values(pendingSyncStatus.getPending())
      });
    });

    const updatePendingOperations = () => {
      setSyncStatus((status) => ({
        ...status,
        pendingOperations: Object.values(pendingSyncStatus.getPending())
      }));
    };

    window.addEventListener('pendingSyncChanged', updatePendingOperations);
    updatePendingOperations();

    return () => {
      unsubscribe();
      window.removeEventListener('pendingSyncChanged', updatePendingOperations);
    };
  }, []);

  const effectiveHasPending = hasPending || syncStatus.pendingOperations.length > 0;
  const effectivePendingCount = Math.max(pendingCount, syncStatus.pendingOperations.length);

  // Determine badge state (subtle colors)
  const getBadgeState = () => {
    if (!isOnline) {
      return {
        color: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        icon: '⚠️',
        text: 'Offline',
        pulse: false
      };
    }

    if (syncStatus.isSyncing) {
      return {
        color: 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-800',
        icon: '🔄',
        text: 'Syncing...',
        pulse: true
      };
    }

    if (effectiveHasPending) {
      return {
        color: 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
        icon: '⏳',
        text: `Pending (${effectivePendingCount})`,
        pulse: false
      };
    }

    return {
      color: 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-800',
      icon: '✓',
      text: 'Synced',
      pulse: false
    };
  };

  const badgeState = getBadgeState();

  const handleClick = () => {
    if (!isOnline || effectiveHasPending) {
      onRetry?.();
    }
  };

  return (
    <div className="relative">
      <motion.button
        onClick={handleClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={`
          flex min-h-[36px] items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border
          ${badgeState.color}
          ${(!isOnline || effectiveHasPending) ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}
          transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        `}
        whileTap={(!isOnline || effectiveHasPending) ? { scale: 0.95 } : {}}
        aria-label={`Sync status: ${badgeState.text}`}
        type="button"
      >
        <motion.span
          className="text-[10px] leading-none"
          animate={badgeState.pulse ? {
            rotate: [0, 360],
          } : {}}
          transition={{
            duration: 1,
            repeat: badgeState.pulse ? Infinity : 0,
            ease: 'linear'
          }}
        >
          {badgeState.icon}
        </motion.span>

        <span>{badgeState.text}</span>

        {badgeState.pulse && (
          <motion.span
            className="absolute inset-0 rounded-full"
            animate={{
              scale: [1, 1.2],
              opacity: [0.5, 0]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeOut'
            }}
            style={{
              background: 'rgba(59, 130, 246, 0.4)',
              zIndex: -1
            }}
          />
        )}
      </motion.button>

      {/* Tooltip */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full right-0 mt-2 px-3 py-2 bg-slate-800 dark:bg-slate-700 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-50"
          >
            {!isOnline && (
              <div>
                <p className="font-semibold">No connection</p>
                <p className="text-slate-300">Changes will sync when online</p>
                {onRetry && <p className="text-slate-300 mt-1">Click to retry</p>}
              </div>
            )}

            {isOnline && syncStatus.isSyncing && (
              <div>
                <p className="font-semibold">Syncing with server...</p>
                <p className="text-slate-300">Your changes are being saved</p>
              </div>
            )}

            {isOnline && effectiveHasPending && (
              <div>
                <p className="font-semibold">{effectivePendingCount} operation{effectivePendingCount > 1 ? 's' : ''} pending</p>
                <p className="text-slate-300">Waiting to sync</p>
                {onRetry && <p className="text-slate-300 mt-1">Click to retry now</p>}
              </div>
            )}

            {isOnline && !syncStatus.isSyncing && !effectiveHasPending && (
              <div>
                <p className="font-semibold">All changes saved</p>
                <p className="text-slate-300">Everything is up to date</p>
              </div>
            )}

            {/* Arrow */}
            <div className="absolute bottom-full right-4 w-2 h-2 bg-slate-800 dark:bg-slate-700 transform rotate-45 translate-y-1" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SyncIndicator;
